'use client';
import React from 'react';
import Image from 'next/image';

// WhatsApp SVG icon
function WhatsAppIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

interface HeroProps {
  language: 'en' | 'ar';
  onQuoteOpen: () => void;
}

const WHATSAPP_GENERAL_URL = `https://wa.me/9647731885522?text=${encodeURIComponent('Hello PLC-IRAQ, I would like to request a quote for industrial automation parts.')}`;

export default function Hero({ language }: HeroProps) {
  return (
    <section
      className="relative bg-onyx overflow-hidden"
      style={{ minHeight: '560px' }}
      dir={language === 'ar' ? 'rtl' : 'ltr'}
      aria-label="Hero section"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <Image
          src="/assets/images/PLC_IRAQ_-_Image_1-1788026633366.jpeg"
          alt="Industrial automation control panel background"
          fill
          className="object-cover opacity-25"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-r from-onyx via-onyx/90 to-onyx/60" />
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 py-20 lg:py-28">
        <div className="max-w-2xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-iraqi-green/20 border border-iraqi-green/40 text-iraqi-green text-sm font-medium px-4 py-1.5 rounded-full mb-6 animate-fade-in">
            <span className="w-2 h-2 rounded-full bg-iraqi-green animate-pulse-slow" />
            {language === 'ar' ? 'متوفر محلياً في البصرة' : 'In Stock Locally in Basra'}
          </div>

          {/* H1 */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 font-space-grotesk animate-slide-up">
            {language === 'ar' ? (
              <>
                قطع أصلية.<br />ضمان التشغيل.
              </>
            ) : (
              <>
                Genuine Parts.<br />Guaranteed Uptime.
              </>
            )}
          </h1>

          {/* Subheadline */}
          <p className="text-slate-300 text-lg leading-relaxed mb-8 font-roboto max-w-xl">
            {language === 'ar' ?'مكونات أتمتة صناعية عالمية المستوى، مسلّمة بسرعة محلية ومدعومة بخبرة هندسية ميدانية. صفر توقف، أقصى ثقة.' :'World-class industrial automation components, delivered with local speed and backed by on-the-ground engineering expertise. Zero downtime, maximum trust.'}
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap gap-4">
            <a
              href={WHATSAPP_GENERAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-iraqi-red hover:bg-primary-dark text-white font-semibold px-8 py-3.5 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg font-space-grotesk"
              aria-label="Request a quote via WhatsApp"
              title="Chat on WhatsApp to request a quote"
            >
              <WhatsAppIcon size={18} />
              {language === 'ar' ? 'طلب عرض سعر' : 'Request a Quote'}
            </a>
            <a
              href="/products"
              className="flex items-center gap-2 border border-white text-white hover:bg-white hover:text-onyx font-semibold px-8 py-3.5 rounded-lg transition-all duration-300 font-space-grotesk"
              aria-label="Browse Catalog"
            >
              {language === 'ar' ? 'تصفح الكتالوج' : 'Browse Catalog'}
              <span>→</span>
            </a>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap gap-8 mt-12 pt-8 border-t border-white/10">
            {[
              { value: '10,000+', label: language === 'ar' ? 'قطعة مخزنة' : 'Parts in Stock' },
              { value: '50+', label: language === 'ar' ? 'علامة تجارية' : 'Brands Stocked' },
              { value: '48hr', label: language === 'ar' ? 'تسليم محلي' : 'Local Delivery' },
            ].map((stat) => (
              <div key={stat.value}>
                <div className="text-2xl font-bold text-white font-space-grotesk">{stat.value}</div>
                <div className="text-sm text-slate-400 font-roboto">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
